import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Stack,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ImagePreview } from '@/components/ImagePreview';
import { ReceiptDataDisplay } from '@/components/ReceiptDataDisplay';
import { LoadingBackdrop } from '@/components/LoadingBackdrop';
import { ErrorAlert } from '@/components/ErrorAlert';
import { getReceipt, saveReceipt, pollForProcessedReceipt } from '@/api/receipt';
import { UploadReceiptResponse, GetReceiptResponse, ProcessingStatus } from '../../generated/receipt/v1/receipt_pb';
import { useReceiptStore } from '@/store/receiptStore';
import { API_BASE_URL } from '@/utils/config';
import { getAccessToken } from '@/api/authInterceptor';

export default function ReceiptDetailPage() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();

  const {
    receiptData,
    originalReceiptData,
    isLoading,
    error,
    setReceiptData,
    setOriginalReceiptData,
    setLoading,
    setError,
  } = useReceiptStore();

  const [imageUri, setImageUri] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('Loading receipt...');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadReceiptImage = async (response: GetReceiptResponse) => {
      if (response.imageUrl) {
        const token = getAccessToken();
        const imageResponse = await fetch(`${API_BASE_URL}${response.imageUrl}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (imageResponse.ok) {
          const blob = await imageResponse.blob();
          const uri = URL.createObjectURL(blob);
          if (!cancelled) setImageUri(uri);
        }
      }
    };

    const populateReceiptData = (response: GetReceiptResponse) => {
      const receiptForStore = {
        receiptId: response.receiptId,
        store: response.store,
        date: response.date,
        payment: response.payment,
        tax: response.tax,
        total: response.total,
        items: response.items,
        saleItems: response.saleItems,
      } as UploadReceiptResponse;

      setReceiptData(receiptForStore);
      setOriginalReceiptData(receiptForStore);
    };

    const fetchReceipt = async () => {
      if (!receiptId) {
        setError('Receipt ID is required');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getReceipt({ receiptId });

        if (cancelled) return;

        if (response.processingStatus === ProcessingStatus.PROCESSING) {
          // Receipt is still being processed — poll until done
          setIsProcessing(true);
          setLoadingMessage('Processing receipt...');

          try {
            const completed = await pollForProcessedReceipt(receiptId);
            if (cancelled) return;

            setIsProcessing(false);
            populateReceiptData(completed);
            await loadReceiptImage(completed);
          } catch (pollErr) {
            if (!cancelled) {
              setIsProcessing(false);
              setError(pollErr instanceof Error ? pollErr.message : 'Processing failed');
            }
          }
        } else if (response.processingStatus === ProcessingStatus.FAILED) {
          setError(response.errorMessage || 'Receipt processing failed');
        } else {
          populateReceiptData(response);
          await loadReceiptImage(response);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load receipt');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReceipt();

    return () => {
      cancelled = true;
      if (imageUri) {
        URL.revokeObjectURL(imageUri);
      }
    };
  }, [receiptId]);

  const hasReceiptEdits = () => {
    if (!originalReceiptData) return false;
    try {
      return JSON.stringify(receiptData) !== JSON.stringify(originalReceiptData);
    } catch {
      return true;
    }
  };

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;

  const getReconciliation = () => {
    if (!receiptData) return { isReconciled: false, difference: 0, calculatedTotal: 0 };
    const itemsTotal = receiptData.items.reduce((sum, item) => sum + item.price, 0);
    const discountsTotal = receiptData.saleItems.reduce((sum, sale) => sum + Math.abs(sale.discount), 0);
    const calculatedTotal = itemsTotal - discountsTotal + receiptData.tax;
    const difference = calculatedTotal - receiptData.total;
    // Compare as decimal strings to avoid floating point precision issues
    const isReconciled = calculatedTotal.toFixed(2) === receiptData.total.toFixed(2);
    return { isReconciled, difference, calculatedTotal };
  };

  const reconciliation = getReconciliation();

  // Clear errors when reconciliation is corrected
  useEffect(() => {
    if (reconciliation.isReconciled && error) {
      setError(null);
    }
  }, [reconciliation.isReconciled]);

  const handleSaveReceipt = async () => {
    if (!receiptData) {
      setError('No receipt data available to save');
      return;
    }

    if (!receiptData.receiptId?.trim()) {
      setError('Missing receipt ID for save');
      return;
    }

    try {
      setLoadingMessage('Saving receipt...');
      setLoading(true);
      setError(null);
      setSaveSuccess(null);

      await saveReceipt({
        receiptId: receiptData.receiptId,
        store: receiptData.store,
        date: receiptData.date,
        payment: receiptData.payment,
        tax: receiptData.tax,
        total: receiptData.total,
        items: receiptData.items,
        saleItems: receiptData.saleItems,
      });

      // Update originalReceiptData to reflect saved state
      setOriginalReceiptData(receiptData);
      setSaveSuccess('Receipt saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToList = () => {
    navigate('/receipts');
  };

  // Auto-save when line items change
  const handleItemChange = useCallback(
    async (updatedData: UploadReceiptResponse | GetReceiptResponse) => {
      if (!updatedData.receiptId?.trim()) return;

      // Check reconciliation before saving
      const itemsTotal = updatedData.items.reduce((sum, item) => sum + item.price, 0);
      const discountsTotal = updatedData.saleItems.reduce(
        (sum, sale) => sum + Math.abs(sale.discount),
        0
      );
      const calculatedTotal = itemsTotal - discountsTotal + updatedData.tax;
      const isReconciled = calculatedTotal.toFixed(2) === updatedData.total.toFixed(2);

      if (!isReconciled) {
        // Don't auto-save if not reconciled - user needs to fix totals first
        return;
      }

      try {
        setLoadingMessage('Saving...');
        setLoading(true);
        setError(null);
        setSaveSuccess(null);

        await saveReceipt({
          receiptId: updatedData.receiptId,
          store: updatedData.store,
          date: updatedData.date,
          payment: updatedData.payment,
          tax: updatedData.tax,
          total: updatedData.total,
          items: updatedData.items,
          saleItems: updatedData.saleItems,
        });

        // Update originalReceiptData to reflect saved state
        setOriginalReceiptData(updatedData as UploadReceiptResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Auto-save failed');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setError, setOriginalReceiptData]
  );

  if (error && !receiptData) {
    return (
      <Box sx={{ py: 3, pl: { xs: 2, md: 0.5 }, pr: { xs: 2, md: 3 }, maxWidth: 1200, overflowX: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            gap: 2,
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={handleBackToList}>
            Back to List
          </Button>
        </Box>
      </Box>
    );
  }

  if (isProcessing || (!receiptData && isLoading)) {
    return <LoadingBackdrop open message={loadingMessage} />;
  }

  if (!receiptData) {
    return null;
  }

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'clip', width: '100%', boxSizing: 'border-box' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            minWidth: 0,
          }}
        >
          {/* Left Panel: Receipt Image */}
          <Box sx={{ flex: { xs: 1, md: 3 }, maxWidth: { md: 400 }, minWidth: 0 }}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%', overflow: 'hidden' }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, textAlign: 'center', fontSize: { xs: '1.1rem', md: '1.25rem' } }}
              >
                Receipt Image
              </Typography>
              {imageUri ? (
                <ImagePreview uri={imageUri} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: { xs: 200, md: 300 },
                  }}
                >
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    No image available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Right Panel: Receipt Data */}
          <Box sx={{ flex: { xs: 1, md: 7 }, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, height: '100%', overflow: 'hidden', maxWidth: '100%' }}>
              <Typography
                variant="h6"
                gutterBottom
                fontWeight="bold"
                align="center"
                sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}
              >
                Receipt Details
              </Typography>
              <ReceiptDataDisplay data={receiptData} onItemChange={handleItemChange} />
            </Paper>
          </Box>
        </Box>

        {/* Success/Error Alerts */}
        <Box sx={{ mt: 2 }}>
          <ErrorAlert message={error} onDismiss={() => setError(null)} />
        </Box>
        {saveSuccess && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" onClose={() => setSaveSuccess(null)}>
              {saveSuccess}
            </Alert>
          </Box>
        )}
        {!reconciliation.isReconciled && receiptData && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">
              Cannot save: Items total minus discounts plus tax ({formatCurrency(reconciliation.calculatedTotal)}) does not equal receipt total ({formatCurrency(receiptData.total)}). Difference: {formatCurrency(reconciliation.difference)}
            </Alert>
          </Box>
        )}

        {/* Footer Actions */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              size="medium"
              onClick={handleSaveReceipt}
              disabled={isLoading || !hasReceiptEdits() || !reconciliation.isReconciled}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Save Changes
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<ShoppingCartIcon />}
              onClick={() => navigate('/lists', { state: { fromReceipt: receiptData } })}
              disabled={isLoading || !receiptData}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Create Shopping List
            </Button>
            <Button
              variant="contained"
              size="medium"
              onClick={handleBackToList}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Back to List
            </Button>
          </Stack>
        </Box>

      <LoadingBackdrop open={isLoading} message={loadingMessage} />
    </Box>
  );
}
